import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import {
  ComplianceCredentialItem,
  ComplianceLicenseItem,
  ComplianceRecertificationItem,
  Course,
  Credential,
  CredentialType,
  CredentialV2,
  EMSID,
  Event,
  ExternalCertificate,
  IBSCCertification,
  LearningPath,
  License,
  LinkedService,
  LiveCertificate,
  NREMTLicense,
  RecertificationCycle,
  RecertificationCycleCategory,
  RecertificationCycleTemplate,
  RecertificationCycleTemplateDetail,
  RecordedCertificate,
  Section,
  UserV2,
  VerificationStatus,
} from 'src/v3/core/types'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  CertificatesRollupData,
  CertificatesSummaryData,
} from 'src/v3/profile/v4/certificates/types'
import dayjs from 'dayjs'

type RecertTypeFilter = 'NREMT' | 'License' | 'Credential' | 'All'
type RecertStatusFilter = 'Active' | 'Completed' | 'Archived' | 'All'
type RecertSort = 'Expiration' | 'Alphabetical A-Z' | 'Alphabetical Z-A'

type AssignmentWithCourses = {
  id: string
  name: string
  status: 'Complete' | 'Incomplete'
  dueDate: string
  dateCompleted: string
  linkedServiceID: string
  courses: {
    id: string
    name: string
    status: 'Complete' | 'Incomplete'
    finalDate: string
  }[]
}

type CourseStatus = {
  id: string
  name: string
  imageName: string
  status: 'Complete' | 'Incomplete'
}

type CourseWithStatus = Course & {
  completed: string[]
  publishedVersion?: number
}

export type NewYorkCourseStatus = CourseStatus & {
  mandatoryStatus?: 'Mandatory' | 'Core Content'
  topicArea?: string
}

export let fetchUser = createAsyncThunk('profile/fetchUserStatus', () =>
  axios.get<UserV2>('/api/v3/profile/user').then(pluckData),
)

export let fetchAssignments = createAsyncThunk(
  'profile/fetchAssignmentsStatus',
  () =>
    axios
      .get<AssignmentWithCourses[]>('/api/v3/profile/assignments-with-courses')
      .then(pluckData),
)

export let fetchCertificates = createAsyncThunk(
  'profile/fetchCertificatesStatus',
  () =>
    axios
      .get<RecordedCertificate[]>('/api/v2/profile/certificates')
      .then(pluckData),
)

export let fetchLiveCertificates = createAsyncThunk(
  'profile/fetchLiveCertificatesStatus',
  () =>
    axios
      .get<LiveCertificate[]>('/api/v2/profile/live-class-certificates')
      .then(pluckData),
)

export let fetchExternalCertificates = createAsyncThunk(
  'profile/fetchExternalCertificatesStatus',
  () =>
    axios
      .get<ExternalCertificate[]>('/api/v2/profile/external-certificates')
      .then(pluckData),
)

export let fetchCertificatesRollup = createAsyncThunk(
  'profile/fetchCertificatesRollupStatus',
  () =>
    axios
      .get<CertificatesRollupData>('/api/profile/certificates/rollup')
      .then(pluckData),
)

export let fetchCertificatesSummary = createAsyncThunk(
  'profile/fetchCertificatesSummaryStatus',
  () =>
    axios
      .get<CertificatesSummaryData>('/api/profile/certificates/summary')
      .then(pluckData),
)

export let fetchCredentials = createAsyncThunk(
  'profile/fetchCredentialsStatus',
  () => axios.get<Credential[]>('/api/v2/profile/credentials').then(pluckData),
)

export let fetchLicenses = createAsyncThunk('profile/fetchLicensesStatus', () =>
  axios.get<License[]>('/api/profile/licenses').then(pluckData),
)

export let fetchUnstartedComplianceRecertItems = createAsyncThunk(
  'profile/fetchUnstartedComplianceRecertItemsStatus',
  () =>
    axios
      .get<any[]>('/api/profile/compliance/unstarted-recertification-items')
      .then(pluckData)
      .then((items) =>
        items.map((item): ComplianceRecertificationItem => {
          let typeObject =
            typeof item.cycleType === 'string'
              ? { [item.cycleType]: {} }
              : item.cycleType
          let [cycleType, cycleDetails] = Object.entries<any>(typeObject)[0]
          let cycleTypeParsed = { kind: cycleType, ...cycleDetails }
          return { ...item, cycleType: cycleTypeParsed }
        }),
      ),
)

export let fetchUnstartedComplianceCredentialItems = createAsyncThunk(
  'profile/fetchUnstartedComplianceCredentialItemsStatus',
  () =>
    axios
      .get<ComplianceCredentialItem[]>(
        '/api/profile/compliance/unstarted-credential-items',
      )
      .then(pluckData),
)

export let fetchUnstartedComplianceLicenseItems = createAsyncThunk(
  'profile/fetchUnstartedComplianceLicenseItemsStatus',
  () =>
    axios
      .get<ComplianceLicenseItem[]>(
        '/api/profile/compliance/unstarted-license-items',
      )
      .then(pluckData),
)

export let fetchIBSC100Courses = createAsyncThunk(
  'profile/fetchIBSC100CoursesStatus',
  () =>
    axios
      .get<CourseStatus[]>('/api/v2/profile/ibsc100/courses')
      .then(pluckData),
)

export let fetchIBSC40Courses = createAsyncThunk(
  'profile/fetchIBSC40CoursesStatus',
  () =>
    axios.get<CourseStatus[]>('/api/v2/profile/ibsc40/courses').then(pluckData),
)

export let fetchLearningPaths = createAsyncThunk(
  'profile/fetchLearningPathsStatus',
  () =>
    axios.get<LearningPath[]>('/api/v2/profile/learning-paths').then(pluckData),
)

export let fetchLearningPathCourses = createAsyncThunk(
  'profile/fetchLearningPathCoursesStatus',
  (id: string) =>
    axios
      .get<CourseStatus[]>(
        `/api/v3/profile/learning-paths/courses?learningPathID=${id}`,
      )
      .then(pluckData),
)

export let fetchNewYorkLearningPathCourses = createAsyncThunk(
  'profile/fetchNewYorkLearningPathCoursesStatus',
  (id: string) =>
    axios
      .get<NewYorkCourseStatus[]>(
        `/api/profile/learning-path/courses/new-york?learningPathID=${id}`,
      )
      .then(pluckData),
)

export let fetchCompletedLearningPaths = createAsyncThunk(
  'profile/fetchCompletedLearningPathsStatus',
  () =>
    axios
      .get<LearningPath[]>('/api/v2/profile/learning-paths-for-user')
      .then(pluckData),
)

export let fetchLinkedServices = createAsyncThunk(
  'profile/fetchLinkedServicesStatus',
  () =>
    axios
      .get<LinkedService[]>('/api/v2/profile/linked-services')
      .then(pluckData),
)

export let fetchIBSC100Certification = createAsyncThunk(
  'profile/fetchIBSC100CertificationStatus',
  () =>
    axios
      .get<IBSCCertification>('/api/v2/profile/ibsc100/certification')
      .then(pluckData),
)

export let fetchIBSC40Certification = createAsyncThunk(
  'profile/fetchIBSC40CertificationStatus',
  () =>
    axios
      .get<IBSCCertification>('/api/v2/profile/ibsc40/certification')
      .then(pluckData),
)

export let fetchNREMTLicense = createAsyncThunk(
  'profile/fetchNREMTLicenseStatus',
  () => axios.get<NREMTLicense>('/api/profile/nremt').then(pluckData),
)

export let fetchNREMTVerification = createAsyncThunk(
  'profile/fetchNREMTVerificationStatus',
  () =>
    axios
      .get<Record<string, VerificationStatus>>(
        '/api/profile/nremt/verification',
      )
      .then(pluckData),
)

export let fetchLicenseVerifications = createAsyncThunk(
  'profile/fetchLicenseVerificationsStatus',
  () =>
    axios
      .get<Record<string, Record<string, VerificationStatus>>>(
        '/api/profile/license/verifications',
      )
      .then(pluckData),
)

export let fetchCredentialVerifications = createAsyncThunk(
  'profile/fetchCredentialVerificationsStatus',
  () =>
    axios
      .get<Record<string, Record<string, VerificationStatus>>>(
        '/api/profile/credential/verifications',
      )
      .then(pluckData),
)

export let fetchEMSID = createAsyncThunk('profile/fetchEMSIDStatus', () =>
  axios.get<EMSID>('/api/profile/ems-id').then(pluckData),
)

export let fetchLicenseRecertificationCycles = createAsyncThunk(
  'profile/fetchLicenseRecertificationCyclesStatus',
  () =>
    axios
      .get<RecertificationCycle[]>(
        '/api/profile/license/recertification-cycles',
      )
      .then(pluckData),
)

export let fetchCredentialRecertificationCycles = createAsyncThunk(
  'profile/fetchCredentialRecertificationCyclesStatus',
  () =>
    axios
      .get<RecertificationCycle[]>(
        '/api/profile/credential/recertification-cycles',
      )
      .then(pluckData),
)

export let fetchNREMTRecertificationCycles = createAsyncThunk(
  'profile/fetchNREMTRecertificationCyclesStatus',
  () =>
    axios
      .get<RecertificationCycle[]>('/api/profile/nremt/recertification-cycles')
      .then(pluckData),
)

export let fetchRecertificationCycleCategories = createAsyncThunk(
  'profile/fetchRecertificationCycleCategoriesStatus',
  (cycleID: string) =>
    axios
      .get<RecertificationCycleCategory[]>(
        `/api/profile/recertification-cycle/${cycleID}/categories`,
      )
      .then(pluckData),
)

export let fetchCompletedEvents = createAsyncThunk(
  'profile/fetchCompletedEventsStatus',
  (cycleID: string) =>
    axios
      .get<Event[]>(`/api/profile/recertification-cycle/${cycleID}/events`)
      .then(pluckData),
)

export let fetchEligibleEvents = createAsyncThunk(
  'profile/fetchEligibleEventsStatus',
  (cycleID: string) =>
    axios
      .get<Event[]>(
        `/api/profile/recertification-cycle/${cycleID}/eligible-events`,
      )
      .then(pluckData),
)

/** The templates the cycle can apply: those with no types, or a type matching the cycle's license, credential or NREMT. */
export let fetchRecertificationCycleTemplates = createAsyncThunk(
  'profile/fetchRecertificationCycleTemplatesStatus',
  (cycleID: string) =>
    axios
      .get<RecertificationCycleTemplate[]>(
        '/api/profile/recertification-cycle/templates',
        { params: { cycleID } },
      )
      .then(pluckData),
)

export let fetchRecertificationCycleTemplateDetail = createAsyncThunk(
  'profile/fetchRecertificationCycleTemplateDetail',
  (templateID: string) =>
    axios
      .get<RecertificationCycleTemplateDetail>(
        `/api/profile/recertification-cycle/template/${templateID}`,
      )
      .then(pluckData),
)

export let fetchCourses = createAsyncThunk('profile/fetchCoursesStatus', () =>
  axios.get<CourseWithStatus[]>('/api/profile/courses').then(pluckData),
)

export let fetchSections = createAsyncThunk('profile/fetchSectionsStatus', () =>
  axios.get<Section[]>('/api/profile/sections').then(pluckData),
)

export type NotificationPreferences = {
  id: string
  userId: string
  credentialsExpiringDigest: boolean
  assignmentsDueDigest: boolean
  managerDigest: boolean
  scheduledAssignmentReminderDays?: number
  createdTime: string
  updatedTime: string
}

export let fetchNotificationPreferences = createAsyncThunk(
  'profile/fetchNotificationPreferencesStatus',
  () =>
    axios
      .get<NotificationPreferences>('/api/profile/settings/notifications')
      .then(pluckData),
)

export let updateNotificationPreferences = createAsyncThunk(
  'profile/updateNotificationPreferencesStatus',
  (preferences: {
    credentialsExpiringDigest: boolean
    assignmentsDueDigest: boolean
    managerDigest: boolean
    scheduledAssignmentReminderDays?: number
  }) =>
    axios
      .put<NotificationPreferences>(
        '/api/profile/settings/notifications',
        preferences,
      )
      .then(pluckData),
)

type ProfileState = {
  recertSearch: string
  recertTypeFilter: RecertTypeFilter
  recertStatusFilter: RecertStatusFilter
  recertSort: RecertSort
  user?: UserV2
  userStatus: Status
  assignments: AssignmentWithCourses[]
  assignmentsStatus: Status
  certificates: RecordedCertificate[]
  certificatesStatus: Status
  liveCertificates: LiveCertificate[]
  liveCertificatesStatus: Status
  externalCertificates: ExternalCertificate[]
  externalCertificatesStatus: Status
  certificatesRollup: CertificatesRollupData
  certificatesRollupStatus: Status
  certificatesSummary?: CertificatesSummaryData
  certificatesSummaryStatus: Status
  credentials: Credential[]
  credentialsStatus: Status
  licenses: License[]
  licensesStatus: Status
  unstartedComplianceRecertItems: ComplianceRecertificationItem[]
  unstartedComplianceRecertItemsStatus: Status
  unstartedComplianceCredentialItems: ComplianceCredentialItem[]
  unstartedComplianceCredentialItemsStatus: Status
  unstartedComplianceLicenseItems: ComplianceLicenseItem[]
  unstartedComplianceLicenseItemsStatus: Status
  ibsc100Courses: CourseStatus[]
  ibsc100CoursesStatus: Status
  ibsc40Courses: CourseStatus[]
  ibsc40CoursesStatus: Status
  learningPaths: LearningPath[]
  learningPathsStatus: Status
  learningPathCourses: Record<string, CourseStatus[]>
  learningPathCoursesStatus: Record<string, Status>
  newYorkLearningPathCourses: Record<string, NewYorkCourseStatus[]>
  newYorkLearningPathCoursesStatus: Record<string, Status>
  completedLearningPaths: LearningPath[]
  completedLearningPathsStatus: Status
  linkedServices: LinkedService[]
  linkedServicesStatus: Status
  ibsc100Certification?: IBSCCertification
  ibsc100CertificationStatus: Status
  ibsc40Certification?: IBSCCertification
  ibsc40CertificationStatus: Status
  nremtLicense?: NREMTLicense
  nremtLicenseStatus: Status
  nremtVerification?: Record<string, VerificationStatus>
  nremtVerificationStatus: Status
  licenseVerifications?: Record<string, Record<string, VerificationStatus>>
  licenseVerificationsStatus: Status
  credentialVerifications?: Record<string, Record<string, VerificationStatus>>
  credentialVerificationsStatus: Status
  emsID?: EMSID
  emsIDStatus: Status
  licenseRecertificationCycles: RecertificationCycle[]
  licenseRecertificationCyclesStatus: Status
  credentialRecertificationCycles: RecertificationCycle[]
  credentialRecertificationCyclesStatus: Status
  nremtRecertificationCycles: RecertificationCycle[]
  nremtRecertificationCyclesStatus: Status
  recertificationCycleCategories: Record<string, RecertificationCycleCategory[]>
  recertificationCycleCategoriesStatus: Record<string, Status>
  completedEvents: Record<string, Event[]>
  completedEventsStatus: Record<string, Status>
  eligibleEvents: Record<string, Event[]>
  eligibleEventsStatus: Record<string, Status>
  recertificationCycleTemplates: Record<string, RecertificationCycleTemplate[]>
  recertificationCycleTemplatesStatus: Record<string, Status>
  recertificationCycleTemplateDetail: Record<
    string,
    RecertificationCycleTemplateDetail
  >
  recertificationCycleTemplateDetailStatus: Record<string, Status>
  courses: CourseWithStatus[]
  coursesStatus: Status
  sections: Section[]
  sectionsStatus: Status
  notificationPreferences?: NotificationPreferences
  notificationPreferencesStatus: Status
  notificationPreferencesAlert?: {
    type: 'success' | 'error'
    message: string
  }
}

export let initialState: ProfileState = {
  recertSearch: '',
  recertTypeFilter: 'All',
  recertStatusFilter: 'All',
  recertSort: 'Expiration',
  userStatus: 'uninitialized',
  assignments: [],
  assignmentsStatus: 'uninitialized',
  certificates: [],
  certificatesStatus: 'uninitialized',
  liveCertificates: [],
  liveCertificatesStatus: 'uninitialized',
  externalCertificates: [],
  externalCertificatesStatus: 'uninitialized',
  certificatesRollup: [],
  certificatesRollupStatus: 'uninitialized',
  certificatesSummaryStatus: 'uninitialized',
  credentials: [],
  credentialsStatus: 'uninitialized',
  licenses: [],
  licensesStatus: 'uninitialized',
  unstartedComplianceRecertItems: [],
  unstartedComplianceRecertItemsStatus: 'uninitialized',
  unstartedComplianceCredentialItems: [],
  unstartedComplianceCredentialItemsStatus: 'uninitialized',
  unstartedComplianceLicenseItems: [],
  unstartedComplianceLicenseItemsStatus: 'uninitialized',
  ibsc100Courses: [],
  ibsc100CoursesStatus: 'uninitialized',
  ibsc40Courses: [],
  ibsc40CoursesStatus: 'uninitialized',
  learningPaths: [],
  learningPathsStatus: 'uninitialized',
  learningPathCourses: {},
  learningPathCoursesStatus: {},
  newYorkLearningPathCourses: {},
  newYorkLearningPathCoursesStatus: {},
  completedLearningPaths: [],
  completedLearningPathsStatus: 'uninitialized',
  linkedServices: [],
  linkedServicesStatus: 'uninitialized',
  ibsc100CertificationStatus: 'uninitialized',
  ibsc40CertificationStatus: 'uninitialized',
  nremtLicenseStatus: 'uninitialized',
  nremtVerificationStatus: 'uninitialized',
  licenseVerificationsStatus: 'uninitialized',
  credentialVerificationsStatus: 'uninitialized',
  emsIDStatus: 'uninitialized',
  licenseRecertificationCycles: [],
  licenseRecertificationCyclesStatus: 'uninitialized',
  credentialRecertificationCycles: [],
  credentialRecertificationCyclesStatus: 'uninitialized',
  nremtRecertificationCycles: [],
  nremtRecertificationCyclesStatus: 'uninitialized',
  recertificationCycleCategories: {},
  recertificationCycleCategoriesStatus: {},
  completedEvents: {},
  completedEventsStatus: {},
  eligibleEvents: {},
  eligibleEventsStatus: {},
  recertificationCycleTemplates: {},
  recertificationCycleTemplatesStatus: {},
  recertificationCycleTemplateDetail: {},
  recertificationCycleTemplateDetailStatus: {},
  courses: [],
  coursesStatus: 'uninitialized',
  sections: [],
  sectionsStatus: 'uninitialized',
  notificationPreferencesStatus: 'uninitialized',
}

export let profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    recertSearchChanged: (state, action: PayloadAction<string>) => {
      state.recertSearch = action.payload
    },
    recertTypeFilterChanged: (
      state,
      action: PayloadAction<RecertTypeFilter>,
    ) => {
      state.recertTypeFilter = action.payload
    },
    recertStatusFilterChanged: (
      state,
      action: PayloadAction<RecertStatusFilter>,
    ) => {
      state.recertStatusFilter = action.payload
    },
    recertSortChanged: (state, action: PayloadAction<RecertSort>) => {
      state.recertSort = action.payload
    },
    learningPathsChanged: (state) => {
      state.learningPaths = []
      state.learningPathsStatus = 'uninitialized'
    },
    clearNotificationPreferencesAlert: (state) => {
      state.notificationPreferencesAlert = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.userStatus = 'pending'
      })
      .addCase(fetchUser.rejected, (state) => {
        state.userStatus = 'rejected'
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.userStatus = 'fulfilled'
        state.user = action.payload
      })
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsStatus = 'pending'
      })
      .addCase(fetchAssignments.rejected, (state) => {
        state.assignmentsStatus = 'rejected'
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignmentsStatus = 'fulfilled'
        state.assignments = action.payload
      })
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesStatus = 'pending'
      })
      .addCase(fetchCertificates.rejected, (state) => {
        state.certificatesStatus = 'rejected'
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.certificatesStatus = 'fulfilled'
        state.certificates = action.payload
      })
      .addCase(fetchLiveCertificates.pending, (state) => {
        state.liveCertificatesStatus = 'pending'
      })
      .addCase(fetchLiveCertificates.rejected, (state) => {
        state.liveCertificatesStatus = 'rejected'
      })
      .addCase(fetchLiveCertificates.fulfilled, (state, action) => {
        state.liveCertificatesStatus = 'fulfilled'
        state.liveCertificates = action.payload
      })
      .addCase(fetchExternalCertificates.pending, (state) => {
        state.externalCertificatesStatus = 'pending'
      })
      .addCase(fetchExternalCertificates.rejected, (state) => {
        state.externalCertificatesStatus = 'rejected'
      })
      .addCase(fetchExternalCertificates.fulfilled, (state, action) => {
        state.externalCertificatesStatus = 'fulfilled'
        state.externalCertificates = action.payload
      })
      .addCase(fetchCertificatesRollup.pending, (state) => {
        state.certificatesRollupStatus = 'pending'
      })
      .addCase(fetchCertificatesRollup.rejected, (state) => {
        state.certificatesRollupStatus = 'rejected'
      })
      .addCase(fetchCertificatesRollup.fulfilled, (state, action) => {
        state.certificatesRollupStatus = 'fulfilled'
        state.certificatesRollup = action.payload
      })
      .addCase(fetchCertificatesSummary.pending, (state) => {
        state.certificatesSummaryStatus = 'pending'
      })
      .addCase(fetchCertificatesSummary.rejected, (state) => {
        state.certificatesSummaryStatus = 'rejected'
      })
      .addCase(fetchCertificatesSummary.fulfilled, (state, action) => {
        state.certificatesSummaryStatus = 'fulfilled'
        state.certificatesSummary = action.payload
      })
      .addCase(fetchCredentials.pending, (state) => {
        state.credentialsStatus = 'pending'
      })
      .addCase(fetchCredentials.rejected, (state) => {
        state.credentialsStatus = 'rejected'
      })
      .addCase(fetchCredentials.fulfilled, (state, action) => {
        state.credentialsStatus = 'fulfilled'
        state.credentials = action.payload
      })
      .addCase(fetchLicenses.pending, (state) => {
        state.licensesStatus = 'pending'
      })
      .addCase(fetchLicenses.rejected, (state) => {
        state.licensesStatus = 'rejected'
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        state.licensesStatus = 'fulfilled'
        state.licenses = action.payload
      })
      .addCase(fetchUnstartedComplianceRecertItems.pending, (state) => {
        state.unstartedComplianceRecertItemsStatus = 'pending'
      })
      .addCase(fetchUnstartedComplianceRecertItems.rejected, (state) => {
        state.unstartedComplianceRecertItemsStatus = 'rejected'
      })
      .addCase(
        fetchUnstartedComplianceRecertItems.fulfilled,
        (state, action) => {
          state.unstartedComplianceRecertItemsStatus = 'fulfilled'
          state.unstartedComplianceRecertItems = action.payload
        },
      )
      .addCase(fetchUnstartedComplianceCredentialItems.pending, (state) => {
        state.unstartedComplianceCredentialItemsStatus = 'pending'
      })
      .addCase(fetchUnstartedComplianceCredentialItems.rejected, (state) => {
        state.unstartedComplianceCredentialItemsStatus = 'rejected'
      })
      .addCase(
        fetchUnstartedComplianceCredentialItems.fulfilled,
        (state, action) => {
          state.unstartedComplianceCredentialItemsStatus = 'fulfilled'
          state.unstartedComplianceCredentialItems = action.payload
        },
      )
      .addCase(fetchUnstartedComplianceLicenseItems.pending, (state) => {
        state.unstartedComplianceLicenseItemsStatus = 'pending'
      })
      .addCase(fetchUnstartedComplianceLicenseItems.rejected, (state) => {
        state.unstartedComplianceLicenseItemsStatus = 'rejected'
      })
      .addCase(
        fetchUnstartedComplianceLicenseItems.fulfilled,
        (state, action) => {
          state.unstartedComplianceLicenseItemsStatus = 'fulfilled'
          state.unstartedComplianceLicenseItems = action.payload
        },
      )
      .addCase(fetchIBSC100Courses.pending, (state) => {
        state.ibsc100CoursesStatus = 'pending'
      })
      .addCase(fetchIBSC100Courses.rejected, (state) => {
        state.ibsc100CoursesStatus = 'rejected'
      })
      .addCase(fetchIBSC100Courses.fulfilled, (state, action) => {
        state.ibsc100CoursesStatus = 'fulfilled'
        state.ibsc100Courses = action.payload
      })
      .addCase(fetchIBSC40Courses.pending, (state) => {
        state.ibsc40CoursesStatus = 'pending'
      })
      .addCase(fetchIBSC40Courses.rejected, (state) => {
        state.ibsc40CoursesStatus = 'rejected'
      })
      .addCase(fetchIBSC40Courses.fulfilled, (state, action) => {
        state.ibsc40CoursesStatus = 'fulfilled'
        state.ibsc40Courses = action.payload
      })
      .addCase(fetchLearningPaths.pending, (state) => {
        state.learningPathsStatus = 'pending'
      })
      .addCase(fetchLearningPaths.rejected, (state) => {
        state.learningPathsStatus = 'rejected'
      })
      .addCase(fetchLearningPaths.fulfilled, (state, action) => {
        state.learningPathsStatus = 'fulfilled'
        state.learningPaths = action.payload
      })
      .addCase(fetchLearningPathCourses.pending, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLearningPathCourses.rejected, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLearningPathCourses.fulfilled, (state, action) => {
        state.learningPathCoursesStatus[action.meta.arg] = 'fulfilled'
        state.learningPathCourses[action.meta.arg] = action.payload
      })
      .addCase(fetchNewYorkLearningPathCourses.pending, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchNewYorkLearningPathCourses.rejected, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchNewYorkLearningPathCourses.fulfilled, (state, action) => {
        state.newYorkLearningPathCoursesStatus[action.meta.arg] = 'fulfilled'
        state.newYorkLearningPathCourses[action.meta.arg] = action.payload
      })
      .addCase(fetchCompletedLearningPaths.pending, (state) => {
        state.completedLearningPathsStatus = 'pending'
      })
      .addCase(fetchCompletedLearningPaths.rejected, (state) => {
        state.completedLearningPathsStatus = 'rejected'
      })
      .addCase(fetchCompletedLearningPaths.fulfilled, (state, action) => {
        state.completedLearningPathsStatus = 'fulfilled'
        state.completedLearningPaths = action.payload
      })
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchIBSC100Certification.pending, (state) => {
        state.ibsc100CertificationStatus = 'pending'
      })
      .addCase(fetchIBSC100Certification.rejected, (state) => {
        state.ibsc100CertificationStatus = 'rejected'
      })
      .addCase(fetchIBSC100Certification.fulfilled, (state, action) => {
        state.ibsc100CertificationStatus = 'fulfilled'
        state.ibsc100Certification = action.payload
      })
      .addCase(fetchIBSC40Certification.pending, (state) => {
        state.ibsc40CertificationStatus = 'pending'
      })
      .addCase(fetchIBSC40Certification.rejected, (state) => {
        state.ibsc40CertificationStatus = 'rejected'
      })
      .addCase(fetchIBSC40Certification.fulfilled, (state, action) => {
        state.ibsc40CertificationStatus = 'fulfilled'
        state.ibsc40Certification = action.payload
      })
      .addCase(fetchNREMTLicense.pending, (state) => {
        state.nremtLicenseStatus = 'pending'
      })
      .addCase(fetchNREMTLicense.rejected, (state) => {
        state.nremtLicenseStatus = 'rejected'
      })
      .addCase(fetchNREMTLicense.fulfilled, (state, action) => {
        state.nremtLicenseStatus = 'fulfilled'
        state.nremtLicense = action.payload
      })
      .addCase(fetchNREMTVerification.pending, (state) => {
        state.nremtVerificationStatus = 'pending'
      })
      .addCase(fetchNREMTVerification.rejected, (state) => {
        state.nremtVerificationStatus = 'rejected'
      })
      .addCase(fetchNREMTVerification.fulfilled, (state, action) => {
        state.nremtVerificationStatus = 'fulfilled'
        state.nremtVerification = action.payload
      })
      .addCase(fetchLicenseVerifications.pending, (state) => {
        state.licenseVerificationsStatus = 'pending'
      })
      .addCase(fetchLicenseVerifications.rejected, (state) => {
        state.licenseVerificationsStatus = 'rejected'
      })
      .addCase(fetchLicenseVerifications.fulfilled, (state, action) => {
        state.licenseVerificationsStatus = 'fulfilled'
        state.licenseVerifications = action.payload
      })
      .addCase(fetchCredentialVerifications.pending, (state) => {
        state.credentialVerificationsStatus = 'pending'
      })
      .addCase(fetchCredentialVerifications.rejected, (state) => {
        state.credentialVerificationsStatus = 'rejected'
      })
      .addCase(fetchCredentialVerifications.fulfilled, (state, action) => {
        state.credentialVerificationsStatus = 'fulfilled'
        state.credentialVerifications = action.payload
      })
      .addCase(fetchEMSID.pending, (state) => {
        state.emsIDStatus = 'pending'
      })
      .addCase(fetchEMSID.rejected, (state) => {
        state.emsIDStatus = 'rejected'
      })
      .addCase(fetchEMSID.fulfilled, (state, action) => {
        state.emsIDStatus = 'fulfilled'
        state.emsID = action.payload
      })
      .addCase(fetchLicenseRecertificationCycles.pending, (state) => {
        state.licenseRecertificationCyclesStatus = 'pending'
      })
      .addCase(fetchLicenseRecertificationCycles.rejected, (state) => {
        state.licenseRecertificationCyclesStatus = 'rejected'
      })
      .addCase(fetchLicenseRecertificationCycles.fulfilled, (state, action) => {
        state.licenseRecertificationCyclesStatus = 'fulfilled'
        state.licenseRecertificationCycles = action.payload
      })
      .addCase(fetchCredentialRecertificationCycles.pending, (state) => {
        state.credentialRecertificationCyclesStatus = 'pending'
      })
      .addCase(fetchCredentialRecertificationCycles.rejected, (state) => {
        state.credentialRecertificationCyclesStatus = 'rejected'
      })
      .addCase(
        fetchCredentialRecertificationCycles.fulfilled,
        (state, action) => {
          state.credentialRecertificationCyclesStatus = 'fulfilled'
          state.credentialRecertificationCycles = action.payload
        },
      )
      .addCase(fetchNREMTRecertificationCycles.pending, (state) => {
        state.nremtRecertificationCyclesStatus = 'pending'
      })
      .addCase(fetchNREMTRecertificationCycles.rejected, (state) => {
        state.nremtRecertificationCyclesStatus = 'rejected'
      })
      .addCase(fetchNREMTRecertificationCycles.fulfilled, (state, action) => {
        state.nremtRecertificationCyclesStatus = 'fulfilled'
        state.nremtRecertificationCycles = action.payload
      })
      .addCase(fetchRecertificationCycleCategories.pending, (state, action) => {
        state.recertificationCycleCategoriesStatus[action.meta.arg] = 'pending'
      })
      .addCase(
        fetchRecertificationCycleCategories.rejected,
        (state, action) => {
          state.recertificationCycleCategoriesStatus[action.meta.arg] =
            'rejected'
        },
      )
      .addCase(
        fetchRecertificationCycleCategories.fulfilled,
        (state, action) => {
          state.recertificationCycleCategoriesStatus[action.meta.arg] =
            'fulfilled'
          state.recertificationCycleCategories[action.meta.arg] = action.payload
        },
      )
      .addCase(fetchCompletedEvents.pending, (state, action) => {
        state.completedEventsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCompletedEvents.rejected, (state, action) => {
        state.completedEventsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCompletedEvents.fulfilled, (state, action) => {
        state.completedEventsStatus[action.meta.arg] = 'fulfilled'
        state.completedEvents[action.meta.arg] = action.payload
      })
      .addCase(fetchEligibleEvents.pending, (state, action) => {
        state.eligibleEventsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEligibleEvents.rejected, (state, action) => {
        state.eligibleEventsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEligibleEvents.fulfilled, (state, action) => {
        state.eligibleEventsStatus[action.meta.arg] = 'fulfilled'
        state.eligibleEvents[action.meta.arg] = action.payload
      })
      .addCase(fetchRecertificationCycleTemplates.pending, (state, action) => {
        state.recertificationCycleTemplatesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchRecertificationCycleTemplates.rejected, (state, action) => {
        state.recertificationCycleTemplatesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(
        fetchRecertificationCycleTemplates.fulfilled,
        (state, action) => {
          state.recertificationCycleTemplatesStatus[action.meta.arg] =
            'fulfilled'
          state.recertificationCycleTemplates[action.meta.arg] = action.payload
        },
      )
      .addCase(
        fetchRecertificationCycleTemplateDetail.pending,
        (state, action) => {
          state.recertificationCycleTemplateDetailStatus[action.meta.arg] =
            'pending'
        },
      )
      .addCase(
        fetchRecertificationCycleTemplateDetail.rejected,
        (state, action) => {
          state.recertificationCycleTemplateDetailStatus[action.meta.arg] =
            'rejected'
        },
      )
      .addCase(
        fetchRecertificationCycleTemplateDetail.fulfilled,
        (state, action) => {
          state.recertificationCycleTemplateDetailStatus[action.meta.arg] =
            'fulfilled'
          state.recertificationCycleTemplateDetail[action.meta.arg] =
            action.payload
        },
      )
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchSections.pending, (state) => {
        state.sectionsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectionsStatus = 'rejected'
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.sectionsStatus = 'fulfilled'
        state.sections = action.payload
      })
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.notificationPreferencesStatus = 'pending'
      })
      .addCase(fetchNotificationPreferences.rejected, (state) => {
        state.notificationPreferencesStatus = 'rejected'
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.notificationPreferencesStatus = 'fulfilled'
        state.notificationPreferences = action.payload
      })
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.notificationPreferencesStatus = 'pending'
        state.notificationPreferencesAlert = undefined
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.notificationPreferencesStatus = 'rejected'
        state.notificationPreferencesAlert = {
          type: 'error',
          message: action.error.message || 'Failed to update preferences',
        }
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.notificationPreferencesStatus = 'fulfilled'
        state.notificationPreferences = action.payload
        state.notificationPreferencesAlert = {
          type: 'success',
          message: 'Preferences updated',
        }
      })
  },
})

export let {
  recertSearchChanged,
  recertTypeFilterChanged,
  recertStatusFilterChanged,
  recertSortChanged,
  learningPathsChanged,
  clearNotificationPreferencesAlert,
} = profileSlice.actions

export let selectRecertSearch = (state: AppRootState) =>
  state.profile.recertSearch
export let selectRecertTypeFilter = (state: AppRootState) =>
  state.profile.recertTypeFilter
export let selectRecertStatusFilter = (state: AppRootState) =>
  state.profile.recertStatusFilter
export let selectRecertSort = (state: AppRootState) => state.profile.recertSort

export let selectUser = (state: AppRootState) => state.profile.user
export let selectUserStatus = (state: AppRootState) => state.profile.userStatus

export let selectAssignments = (state: AppRootState) =>
  state.profile.assignments.map((ca) => ({
    ...ca,
    name: ca.name || 'Unnamed Assignment',
  }))
export let selectAssignmentsStatus = (state: AppRootState) =>
  state.profile.assignmentsStatus

export let selectCertificates = (state: AppRootState) =>
  state.profile.certificates
export let selectCertificatesStatus = (state: AppRootState) =>
  state.profile.certificatesStatus

export let selectLiveCertificates = (state: AppRootState) =>
  state.profile.liveCertificates
export let selectLiveCertificatesStatus = (state: AppRootState) =>
  state.profile.liveCertificatesStatus

export let selectExternalCertificates = (state: AppRootState) =>
  state.profile.externalCertificates
export let selectExternalCertificatesStatus = (state: AppRootState) =>
  state.profile.externalCertificatesStatus

export let selectCertificatesRollup = (state: AppRootState) =>
  state.profile.certificatesRollup
export let selectCertificatesRollupStatus = (state: AppRootState) =>
  state.profile.certificatesRollupStatus

export let selectCertificatesSummary = (state: AppRootState) =>
  state.profile.certificatesSummary
export let selectCertificatesSummaryStatus = (state: AppRootState) =>
  state.profile.certificatesSummaryStatus

export let selectCredentials = (state: AppRootState) =>
  state.profile.credentials.map((c) => ({
    ...c,
    credentialType: typeof c.type === 'string' ? c.type : c.type.other,
  }))
export let selectCredentialsV2 = (state: AppRootState) =>
  state.profile.credentials.map((c): CredentialV2 => ({
    ...c,
    type: {
      kind:
        typeof c.type === 'string'
          ? (c.type.toUpperCase() as CredentialType['kind'])
          : 'Other',
      value: typeof c.type === 'string' ? '' : c.type.other,
    },
  }))
export let selectCredentialsStatus = (state: AppRootState) =>
  state.profile.credentialsStatus

export let selectLicenses = (state: AppRootState) => state.profile.licenses
export let selectLicensesStatus = (state: AppRootState) =>
  state.profile.licensesStatus

export let selectUnstartedComplianceRecertItems = (state: AppRootState) =>
  state.profile.unstartedComplianceRecertItems
export let selectUnstartedComplianceRecertItemsStatus = (state: AppRootState) =>
  state.profile.unstartedComplianceRecertItemsStatus

export let selectUnstartedComplianceCredentialItems = (state: AppRootState) =>
  state.profile.unstartedComplianceCredentialItems
export let selectUnstartedComplianceCredentialItemsStatus = (
  state: AppRootState,
) => state.profile.unstartedComplianceCredentialItemsStatus

export let selectUnstartedComplianceLicenseItems = (state: AppRootState) =>
  state.profile.unstartedComplianceLicenseItems
export let selectUnstartedComplianceLicenseItemsStatus = (
  state: AppRootState,
) => state.profile.unstartedComplianceLicenseItemsStatus

export let selectIBSC100Courses = (state: AppRootState) =>
  state.profile.ibsc100Courses
export let selectIBSC100CoursesStatus = (state: AppRootState) =>
  state.profile.ibsc100CoursesStatus

export let selectIBSC40Courses = (state: AppRootState) =>
  state.profile.ibsc40Courses
export let selectIBSC40CoursesStatus = (state: AppRootState) =>
  state.profile.ibsc40CoursesStatus

export let selectLearningPaths = (state: AppRootState) =>
  state.profile.learningPaths
export let selectLearningPathsStatus = (state: AppRootState) =>
  state.profile.learningPathsStatus

export let selectLearningPathCourses = (state: AppRootState) =>
  state.profile.learningPathCourses
export let selectLearningPathCoursesStatus = (state: AppRootState) =>
  state.profile.learningPathCoursesStatus

export let selectNewYorkLearningPathCourses = (state: AppRootState) =>
  state.profile.newYorkLearningPathCourses
export let selectNewYorkLearningPathCoursesStatus = (state: AppRootState) =>
  state.profile.newYorkLearningPathCoursesStatus

export let selectCompletedLearningPaths = (state: AppRootState) =>
  state.profile.completedLearningPaths
export let selectCompletedLearningPathsStatus = (state: AppRootState) =>
  state.profile.completedLearningPathsStatus

export let selectLinkedServices = (state: AppRootState) =>
  state.profile.linkedServices
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.profile.linkedServicesStatus

export let selectIBSC100Certification = (state: AppRootState) =>
  state.profile.ibsc100Certification
export let selectIBSC100CertificationStatus = (state: AppRootState) =>
  state.profile.ibsc100CertificationStatus

export let selectIBSC40Certification = (state: AppRootState) =>
  state.profile.ibsc40Certification
export let selectIBSC40CertificationStatus = (state: AppRootState) =>
  state.profile.ibsc40CertificationStatus

export let selectNREMTLicense = (state: AppRootState) =>
  !state.profile.nremtLicense
    ? undefined
    : ({
        ...state.profile.nremtLicense,
        type:
          { r: 'EMR', e: 'EMT', a: 'AEMT', m: 'Paramedic' }[
            state.profile.nremtLicense.nremtidNumber[0]?.toLowerCase() || ''
          ] || 'Unknown Type',
      } as NREMTLicense)
export let selectNREMTLicenseStatus = (state: AppRootState) =>
  state.profile.nremtLicenseStatus
export let selectEMSID = (state: AppRootState) => state.profile.emsID
export let selectEMSIDStatus = (state: AppRootState) =>
  state.profile.emsIDStatus
export let selectLicenseData = (state: AppRootState) => ({
  primaryLicense: state.profile.licenses.find(
    (l) => l.displayRank.toLowerCase() === 'primary',
  ),
  nremtNumber: state.profile.nremtLicense?.nremtidNumber,
  nremtExpiration: state.profile.nremtLicense?.expiration,
  emsID: state.profile.emsID?.emsidNumber,
})

export let selectRecertificationCycles = (state: AppRootState) => [
  ...state.profile.licenseRecertificationCycles,
  ...state.profile.credentialRecertificationCycles,
  ...state.profile.nremtRecertificationCycles,
]
export let selectLicenseRecertificationCycles = (state: AppRootState) =>
  state.profile.licenseRecertificationCycles
export let selectLicenseRecertificationCyclesStatus = (state: AppRootState) =>
  state.profile.licenseRecertificationCyclesStatus
export let selectCredentialRecertificationCycles = (state: AppRootState) =>
  state.profile.credentialRecertificationCycles
export let selectCredentialRecertificationCyclesStatus = (
  state: AppRootState,
) => state.profile.credentialRecertificationCyclesStatus
export let selectNREMTRecertificationCycles = (state: AppRootState) =>
  state.profile.nremtRecertificationCycles
export let selectNREMTRecertificationCyclesStatus = (state: AppRootState) =>
  state.profile.nremtRecertificationCyclesStatus

export let selectRecertificationCategories = (state: AppRootState) =>
  state.profile.recertificationCycleCategories
export let selectRecertificationCategoriesStatus = (state: AppRootState) =>
  state.profile.recertificationCycleCategoriesStatus

export let selectCompletedEvents = (state: AppRootState) =>
  state.profile.completedEvents
export let selectCompletedEventsStatus = (state: AppRootState) =>
  state.profile.completedEventsStatus

export let selectEligibleEventsByRecertCycleID =
  (id: string) => (state: AppRootState) =>
    state.profile.eligibleEvents[id] ?? []
export let selectEligibleEventsStatus = (state: AppRootState) =>
  state.profile.eligibleEventsStatus

export let selectRecertificationCycleTemplates =
  (cycleID: string) => (state: AppRootState) =>
    state.profile.recertificationCycleTemplates[cycleID]
export let selectRecertificationCycleTemplatesStatus =
  (cycleID: string) => (state: AppRootState) =>
    state.profile.recertificationCycleTemplatesStatus[cycleID] ??
    'uninitialized'
export let selectRecertificationCycleTemplateDetail =
  (templateID: string) => (state: AppRootState) =>
    state.profile.recertificationCycleTemplateDetail[templateID]
export let selectRecertificationCycleTemplateDetailStatus =
  (templateID: string) => (state: AppRootState) =>
    state.profile.recertificationCycleTemplateDetailStatus[templateID]

export let selectCourses = (state: AppRootState) => state.profile.courses
export let selectSortedCourses = (cycleID: string) => (state: AppRootState) => {
  let cycle = selectRecertificationCycles(state).find((c) => c.id === cycleID)
  return state.profile.courses
    .map((c) => ({
      ...c,
      completed:
        !!cycle &&
        c.completed.some(
          (date) =>
            dayjs(date).isBefore(cycle.expirationDate) &&
            dayjs(date).isAfter(cycle.issueDate),
        ),
    }))
    .toSorted((a, b) =>
      a.completed === b.completed ? 0 : a.completed ? -1 : 1,
    )
}
export let selectCoursesStatus = (state: AppRootState) =>
  state.profile.coursesStatus
export let selectSections = (state: AppRootState) => state.profile.sections
export let selectSectionsStatus = (state: AppRootState) =>
  state.profile.sectionsStatus

export let selectNREMTVerification = (state: AppRootState) =>
  state.profile.nremtVerification
export let selectNREMTVerificationStatus = (state: AppRootState) =>
  state.profile.nremtVerificationStatus
export let selectLicenseVerifications = (state: AppRootState) =>
  state.profile.licenseVerifications
export let selectLicenseVerificationsStatus = (state: AppRootState) =>
  state.profile.licenseVerificationsStatus
export let selectCredentialVerifications = (state: AppRootState) =>
  state.profile.credentialVerifications
export let selectCredentialVerificationsStatus = (state: AppRootState) =>
  state.profile.credentialVerificationsStatus

export let selectNotificationPreferences = (state: AppRootState) =>
  state.profile.notificationPreferences
export let selectNotificationPreferencesStatus = (state: AppRootState) =>
  state.profile.notificationPreferencesStatus
export let selectNotificationPreferencesAlert = (state: AppRootState) =>
  state.profile.notificationPreferencesAlert

export default profileSlice.reducer
